import {
  getCategoriesOptions,
  getCategoriesQueryKey,
  getGenresOptions,
  getGenresQueryKey,
  getRaresOptions,
  getRaresQueryKey,
  getGendersOptions,
  getGendersQueryKey
} from "@/client/@tanstack/react-query.gen";
import { readCache } from "@/store/action";
import { translate } from "@/lib";
import type {
  GetCategoriesResponse,
  GetGenresResponse,
  GetRaresResponse,
  GetGendersResponse
} from "@/client/types.gen";

// Types for metadata items
export type ApiCategory = GetCategoriesResponse[number];
export type ApiGenre = GetGenresResponse[number];
export type ApiRare = GetRaresResponse[number];
export type ApiGender = GetGendersResponse[number];

// Query options exports for use in App.tsx
export const useCategoriesOptions = () => getCategoriesOptions();
export const useGenresOptions = () => getGenresOptions();
export const useRaresOptions = () => getRaresOptions();
export const useGendersOptions = () => getGendersOptions();

// Cache readers
export function getCategories(): GetCategoriesResponse | undefined {
  return readCache<ReturnType<typeof getCategoriesQueryKey>, GetCategoriesResponse>(
    getCategoriesQueryKey()
  );
}

export function getGenres(): GetGenresResponse | undefined {
  return readCache<ReturnType<typeof getGenresQueryKey>, GetGenresResponse>(
    getGenresQueryKey()
  );
}

export function getRares(): GetRaresResponse | undefined {
  return readCache<ReturnType<typeof getRaresQueryKey>, GetRaresResponse>(
    getRaresQueryKey()
  );
}

export function getGenders(): GetGendersResponse | undefined {
  return readCache<ReturnType<typeof getGendersQueryKey>, GetGendersResponse>(
    getGendersQueryKey()
  );
}

// Lookup helpers by ID
export function getCategoryById(id: number): ApiCategory | undefined {
  const categories = getCategories();
  return categories?.find(cat => cat.id === id);
}

export function getGenreById(id: number): ApiGenre | undefined {
  const genres = getGenres();
  return genres?.find(genre => genre.id === id);
}

export function getRareById(id: number): ApiRare | undefined {
  const rares = getRares();
  return rares?.find(rare => rare.id === id);
}

export function getGenderById(id: number): ApiGender | undefined {
  const genders = getGenders();
  return genders?.find(gender => gender.id === id);
}

// Display name helper - use transify key to look up translation, fallback to name
export function getDisplayName(item: { transify?: string; name: string } | undefined): string {
  if (!item) return '';
  return (item.transify ? translate(item.transify) : '') || item.name;
}

// Get category display name by ID
export function getCategoryName(id: number): string {
  const category = getCategoryById(id);
  return getDisplayName(category);
}

// Get genre display name by ID
export function getGenreName(id: number): string {
  const genre = getGenreById(id);
  return getDisplayName(genre);
}

// Get rare display name and color by ID
export function getRareInfo(id: number): { name: string; color: string } {
  const rare = getRareById(id);
  return {
    name: getDisplayName(rare),
    color: rare?.color || '#95a5a6', // Default gray if not found
  };
}

// Get gender display name by ID
export function getGenderName(id: number): string {
  const gender = getGenderById(id);
  return getDisplayName(gender);
}
